import React, { useState, useEffect } from 'react';
import { 
  Upload, 
  Button, 
  Table, 
  message, 
  Card, 
  Typography, 
  Space, 
  Alert, 
  Spin,
  Tooltip,
  Modal,
  Popconfirm
} from 'antd';
import { 
  UploadOutlined, 
  DownloadOutlined, 
  DeleteOutlined,
  InfoCircleOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import * as XLSX from 'xlsx';
import staffService from '../../services/staffService';

const { Title, Text, Paragraph } = Typography;

/**
 * StaffUpload - Component for uploading organizational staff with full replacement
 */
const StaffUpload = ({ orgId, orgName }) => {
  const [currentStaff, setCurrentStaff] = useState([]);
  const [parsedData, setParsedData] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);
  const [replaceMode, setReplaceMode] = useState(false);
  const [replacingAllStaff, setReplacingAllStaff] = useState(false);

  // Load current staff on component mount
  useEffect(() => {
    if (orgId) {
      loadCurrentStaff();
    }
  }, [orgId]);

  const loadCurrentStaff = async () => {
    try {
      setLoading(true);
      const staff = await staffService.getOrganizationStaff(orgId);
      setCurrentStaff(staff);
    } catch (error) {
      console.error('Error loading staff:', error);
      message.error('Failed to load current staff data');
    } finally {
      setLoading(false);
    }
  };

  // Parse uploaded file (CSV or Excel)
  const parseFile = (file) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        let data;
        const fileExtension = file.name.split('.').pop().toLowerCase();
        
        if (fileExtension === 'csv') {
          // Parse CSV
          const text = e.target.result;
          const lines = text.split('\n').filter(line => line.trim());
          const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
          
          data = lines.slice(1).map(line => {
            const values = line.split(',').map(v => v.trim().replace(/"/g, ''));
            const row = {};
            headers.forEach((header, index) => {
              row[header] = values[index] || '';
            });
            return row;
          });
        } else {
          // Parse Excel
          const workbook = XLSX.read(e.target.result, { type: 'binary' });
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          data = XLSX.utils.sheet_to_json(firstSheet);
        }

        console.log('📊 Parsed staff data:', data.length, 'records');
        setParsedData(data);
        message.success(`Parsed ${data.length} staff records`);
        
      } catch (error) {
        console.error('Error parsing file:', error);
        message.error('Failed to parse file. Please check format.');
      }
    };

    if (file.name.endsWith('.csv')) {
      reader.readAsText(file);
    } else {
      reader.readAsBinaryString(file);
    }
  };

  // Upload staff to database
  const handleUploadStaff = async () => {
    if (!parsedData || parsedData.length === 0) {
      message.error('No staff data to upload');
      return;
    }

    try {
      setUploading(true);
      const results = await staffService.bulkUploadStaff(parsedData, orgId);
      
      setUploadResults(results);
      setUploadComplete(true);
      
      if (results.successful > 0) {
        message.success(`Successfully uploaded ${results.successful} staff records!`);
        await loadCurrentStaff(); // Reload to show new data
      }
      
    } catch (error) {
      console.error('Error uploading staff:', error);
      message.error(`Failed to upload staff: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  // Replace all staff
  const handleReplaceAllStaff = async () => {
    if (!parsedData || parsedData.length === 0) {
      message.error('Please upload a staff file first before replacing');
      return;
    }

    try {
      setReplacingAllStaff(true);
      const results = await staffService.bulkUploadStaff(parsedData, orgId);
      
      setUploadResults(results);
      setUploadComplete(true);
      setReplaceMode(false);
      
      if (results.successful > 0) {
        message.success(`Successfully replaced all staff! Deleted: ${results.deleted}, Uploaded: ${results.successful}`);
        await loadCurrentStaff();
      }
      
    } catch (error) {
      console.error('Error replacing staff:', error);
      message.error(`Failed to replace staff: ${error.message}`);
    } finally {
      setReplacingAllStaff(false);
    }
  };

  // Download template
  const downloadTemplate = () => {
    const template = staffService.generateStaffTemplate();
    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'staff_upload_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    message.success('Template downloaded');
  };

  // Export current staff
  const exportCurrentStaff = async () => {
    try {
      const csvData = await staffService.exportStaffToCSV(orgId);
      const blob = new Blob([csvData], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${orgName || 'organization'}_staff_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      message.success('Staff data exported');
    } catch (error) {
      console.error('Error exporting staff:', error);
      message.error('Failed to export staff data');
    }
  };

  // Reset upload state
  const resetUpload = () => {
    setParsedData(null);
    setUploadComplete(false);
    setUploadResults(null);
    setReplaceMode(false);
  };

  // Table columns for displaying current staff
  const currentStaffColumns = [
    {
      title: 'Name',
      dataIndex: 'last_name',
      key: 'name',
      render: (lastName, record) => `${record.first_name} ${lastName}`,
      width: 200
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 250
    },
    {
      title: 'Mobile',
      dataIndex: 'mobile_phone',
      key: 'mobile_phone',
      width: 150
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      width: 150
    },
    {
      title: 'Site',
      dataIndex: 'site_name',
      key: 'site_name',
      width: 150
    },
    {
      title: 'Department',
      dataIndex: 'department',
      key: 'department',
      width: 150
    }
  ];

  // Table columns for displaying parsed data
  const parsedDataColumns = staffService.STAFF_REQUIRED_COLUMNS.slice(0, 8).map(col => ({
    title: col.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
    dataIndex: col,
    key: col,
    width: 120,
    render: (text) => text || <Text type="secondary">-</Text>
  }));

  return (
    <div className="staff-upload">
      <Card 
        title={
          <Space>
            <Title level={4} style={{ margin: 0 }}>
              Staff Management
            </Title>
            <InfoCircleOutlined />
          </Space>
        }
        style={{ marginBottom: '16px' }}
      >
        <Paragraph>
          View current staff, export staff data, and upload new staff for the organization. 
          <strong> Staff upload will completely replace all existing staff records.</strong>
        </Paragraph>
        
        <Alert
          type="info"
          message="Full Replacement Model"
          description="To replace all staff: (1) Export current staff as backup, (2) Upload new CSV or Excel file below, (3) Choose 'Replace All Staff' from the preview options."
          style={{ marginBottom: '16px' }}
        />
      </Card>

      {/* Current Staff Display */}
      <Card title="Current Staff" style={{ marginBottom: '16px' }}>
        <Space style={{ marginBottom: '16px' }}>
          <Button 
            icon={<DownloadOutlined />} 
            onClick={exportCurrentStaff}
            disabled={currentStaff.length === 0}
          >
            Export Current Staff
          </Button>
          
          <Button 
            icon={<DownloadOutlined />}
            onClick={downloadTemplate}
          >
            Download Template
          </Button>
        </Space>

        {loading ? (
          <Spin size="large" />
        ) : currentStaff.length > 0 ? (
          <Table
            dataSource={currentStaff}
            columns={currentStaffColumns}
            rowKey="id"
            pagination={{ pageSize: 10 }}
            scroll={{ x: 1200 }}
            size="small"
          />
        ) : (
          <Alert
            type="info"
            message="No Staff Records"
            description="This organization has no staff uploaded yet. Use the upload section below to add staff."
            showIcon
          />
        )}
      </Card>

      {/* Replace All Staff Confirmation */}
      <Modal
        title="⚠️ Replace All Staff - Confirmation Required"
        open={replaceMode}
        onCancel={() => setReplaceMode(false)}
        footer={[
          <Button key="cancel" onClick={() => setReplaceMode(false)}>
            Cancel
          </Button>,
          <Popconfirm
            key="confirm"
            title="Replace All Staff"
            description={`This action will permanently delete all ${currentStaff.length} existing staff records and replace them with the new upload. This cannot be undone.`}
            onConfirm={handleReplaceAllStaff}
            okText="REPLACE ALL STAFF"
            okType="danger"
            cancelText="Cancel"
            icon={<ExclamationCircleOutlined style={{ color: 'red' }} />}
          >
            <Button 
              type="primary" 
              danger
              loading={replacingAllStaff}
            >
              Replace All Staff
            </Button>
          </Popconfirm>
        ]}
      >
        <Alert
          type="warning"
          message="Data Replacement Warning"
          description={
            <div>
              <p><strong>Current staff:</strong> {currentStaff.length} records</p>
              <p><strong>New staff to upload:</strong> {parsedData?.length || 0}</p>
              <p><strong>This will permanently delete all existing staff and replace with new data.</strong></p>
            </div>
          }
          showIcon
          style={{ marginBottom: '16px' }}
        />
      </Modal>

      {/* Upload Complete */}
      {uploadComplete && (
        <Card 
          title={replaceMode ? 'Staff Replacement Complete' : 'Upload Complete'}
          style={{ marginBottom: '16px' }}
        >
          <Alert
            type="success"
            message={`Successfully processed ${uploadResults?.successful || 0} staff records`}
            description={uploadResults?.deleted ? `Deleted ${uploadResults.deleted} existing records` : null}
            showIcon
            action={
              <Button size="small" onClick={resetUpload}>
                Upload More Staff
              </Button>
            }
          />
        </Card>
      )}

      {/* File Upload */}
      <Card title={replaceMode ? "Upload New Staff (Replace All)" : "Upload Staff"}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Upload
            accept=".csv,.xlsx,.xls"
            beforeUpload={(file) => {
              parseFile(file);
              return false; // Prevent auto upload
            }}
            showUploadList={false}
            disabled={uploading || replacingAllStaff}
          >
            <Button icon={<UploadOutlined />} size="large">
              Select CSV or Excel File
            </Button>
          </Upload>

          {parsedData && (
            <div>
              <Title level={5}>Parsed Data Preview ({parsedData.length} records)</Title>
              <Table
                dataSource={parsedData.slice(0, 5)}
                columns={parsedDataColumns}
                pagination={false}
                scroll={{ x: 1200 }}
                size="small"
              />
              {parsedData.length > 5 && (
                <Text type="secondary">... and {parsedData.length - 5} more records</Text>
              )}

              <div style={{ marginTop: '16px' }}>
                <Space>
                  <Button 
                    type="primary"
                    icon={<UploadOutlined />}
                    onClick={handleUploadStaff}
                    loading={uploading}
                    disabled={replacingAllStaff}
                  >
                    Upload Staff
                  </Button>

                  <Tooltip title="Upload a CSV file first to enable staff replacement">
                    <Button 
                      type="primary"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => setReplaceMode(true)}
                      disabled={!parsedData || currentStaff.length === 0}
                    >
                      Replace All Staff
                    </Button>
                  </Tooltip>
                </Space>
              </div>
            </div>
          )}

          {(uploading || replacingAllStaff) && (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <Spin size="large" />
              <Text>{replacingAllStaff ? 'Replacing all staff...' : 'Uploading staff...'}</Text>
            </div>
          )}
        </Space>
      </Card>
    </div>
  );
};

export default StaffUpload;
